import {
  CompetitionLevelSchema,
  postOrganizationsByOrgIdAssociateOfficialTeams,
  SportTypeSchema,
} from "@aura-frontend/omnibus-az-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Input,
  Select,
} from "@win-reality/react-ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { usaStates } from "typed-usa-states";
import * as z from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { competitionLevelsOptions } from "@/lib/competition-level-options";
import { sportsOptions } from "@/lib/sports-options";
import { cn, removeOrgPrefix } from "@/lib/utils";

import SearchMachineCombobox from "../search/search-machine-combobox";
import SearchTeamCombobox from "../search/search-team-combobox";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { useOfficialTeamData } from "./useOfficialTeamData";

interface AccountCreationFormProps {
  defaultValues?: z.infer<typeof accountFormSchema>;
  onSubmit: (data: z.infer<typeof accountFormSchema>) => void;
  mode: "creation" | "edit";
  organizationId?: string;
  setScrollContainer: React.Dispatch<
    React.SetStateAction<HTMLDivElement | null>
  >;
  showDivider: boolean;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
}

export const accountFormSchema = z.object({
  accountName: z.string().min(1, "Required").max(100),
  sport: z.enum(SportTypeSchema.enum),
  competitionLevel: z.enum(CompetitionLevelSchema.enum),
  city: z.string().min(1, "Required"),
  state: z.string().min(1, "Required"),
  teamType: z.enum(["CSB", "PG"]).default("CSB"),
  team: z
    .object({
      id: z.string(),
      name: z.string(),
      externalId: z.string(),
    })
    .optional(),
});

const stateItems = usaStates.map((option) => ({
  label: option.name,
  value: option.abbreviation,
}));

export function AccountForm({
  defaultValues,
  onSubmit,
  mode = "creation",
  organizationId,
  setScrollContainer,
  showDivider,
  isPending,
  onOpenChange,
}: AccountCreationFormProps) {
  const form = useForm<z.infer<typeof accountFormSchema>>({
    resolver: zodResolver(accountFormSchema),
    defaultValues,
  });

  const { data: officialTeamData, refetch: refetchTeamData } =
    useOfficialTeamData(organizationId ?? "");

  useEffect(() => {
    if (officialTeamData) {
      form.setValue(
        "teamType",
        officialTeamData.externalId.startsWith("CSB") ? "CSB" : "PG",
      );

      form.setValue("team", {
        id: officialTeamData.id,
        name: officialTeamData.name,
        externalId: officialTeamData.externalId,
      });
    }
  }, [officialTeamData, form]);

  const associateTeam = useMutation({
    mutationFn: (teamId?: string) =>
      postOrganizationsByOrgIdAssociateOfficialTeams({
        path: { org_id: removeOrgPrefix(organizationId!) },
        body: { externalIds: teamId ? [teamId] : [] },
      }),
  });

  const handleTeamSelect = async (
    selectedTeam:
      | {
          id: string;
          name: string;
          externalId: string;
        }
      | undefined,
  ) => {
    if (associateTeam.isPending) return;

    // Keeping the previous team to revert if the mutation fails
    const previousTeam = form.getValues("team");

    if (selectedTeam) {
      form.setValue("team", selectedTeam);
    } else {
      form.setValue("team", undefined);
    }

    try {
      await associateTeam.mutateAsync(selectedTeam?.externalId);
      refetchTeamData();
    } catch (error) {
      form.setValue("team", previousTeam);
      console.error("Something went wrong", error);
    }
  };

  const handleSubmit = (data: z.infer<typeof accountFormSchema>) => {
    onSubmit(data);
  };

  return (
    <>
      <DialogScrollableContent setScrollContainer={setScrollContainer}>
        <DialogHeader>
          <DialogTitle>
            Organization {mode === "creation" ? "Creation" : "Edit"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form className="h-full flex flex-col">
            <div className={cn("flex-1 w-full")}>
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="accountName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Organization Name:</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Organization Name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sport"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Sport:</FormLabel>
                      <FormControl>
                        <Select
                          items={[...sportsOptions]}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder="Select a sport"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="competitionLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Competition Level:</FormLabel>
                      <FormControl>
                        <Select
                          items={[...competitionLevelsOptions]}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder="Select a competition level"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>City:</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>State:</FormLabel>
                      <FormControl>
                        <Select
                          items={stateItems}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder="Select a state"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {mode === "edit" && (
                  <>
                    <FormField
                      control={form.control}
                      name="teamType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Team:</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={(e) => {
                                field.onChange(e);
                                form.resetField("team");
                              }}
                              defaultValue={field.value}
                              value={field.value}
                              className="flex gap-4"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="CSB" id="cs" />
                                <Label htmlFor="cs">College Splits ID</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="PG" id="pg" />
                                <Label htmlFor="pg">PG ID</Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="team"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-2">
                          <FormControl>
                            <SearchTeamCombobox
                              {...field}
                              teamType={form.watch("teamType")}
                              selectedTeam={field.value}
                              valueSearchPlaceholder="Search teams"
                              onChange={handleTeamSelect}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {organizationId && (
                  <FormItem>
                    <FormLabel>Yakkertech Hardware IDs:</FormLabel>
                    <FormControl>
                      <SearchMachineCombobox
                        valueSearchPlaceholder="Search hardware IDs"
                        organizationId={organizationId}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              </div>
            </div>
          </form>
        </Form>
      </DialogScrollableContent>

      <DialogFooter showDivider={showDivider}>
        <div className="flex w-full justify-between">
          <Button
            variant="ghost"
            type="button"
            className="underline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="secondary-flat-light"
            form="account-create-form"
            isLoading={isPending}
            onClick={() => form.handleSubmit(handleSubmit)()}
          >
            {mode === "creation" ? "Generate Link" : "Save"}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
